import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Container, 
  Box, 
  IconButton, 
  Menu, 
  MenuItem,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Avatar
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import { useCategories } from '../../hooks';
import { Category } from '../../types';

const Navbar: React.FC = () => {
  const { categories, loading, selectedCategory, setSelectedCategory } = useCategories();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  const handleCategoryClick = (category: Category) => {
    setSelectedCategory(category.id);
    const identifier = category.slug || category.id;
    navigate(`/category/${identifier}`);
    setMobileMenuOpen(false);
    setAnchorEl(null);
  };

  const handleHomeClick = () => {
    setSelectedCategory(null);
    navigate('/');
    setMobileMenuOpen(false);
    setAnchorEl(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Only show a subset of categories in the navbar, the rest in the dropdown
  const visibleCategories = !isMobile ? categories.slice(0, 5) : [];
  const dropdownCategories = !isMobile ? categories.slice(5) : categories;
  
  return (
    <AppBar position="sticky" color="primary" elevation={2}>
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
          {/* Logo and site name */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar 
              src="/Akademikadro_Logo.jpeg" 
              alt="News Logo"
              sx={{ 
                width: 40, 
                height: 40, 
                marginRight: 1,
                bgcolor: 'white',
                border: '2px solid white'
              }}
            >
              N
            </Avatar>
            <Typography
              variant="h5"
              noWrap
              component={Link}
              to="/"
              onClick={handleHomeClick}
              sx={{
                fontWeight: 700,
                letterSpacing: '.1rem',
                color: 'white',
                textDecoration: 'none',
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                lineHeight: 1,
                marginTop: '1px'
              }}
            >
              Akademi Kadro
            </Typography>
          </Box>

          {/* Main navigation - desktop */}
          {!isMobile && (
            <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'center' }}>
              <Button
                color="inherit"
                onClick={handleHomeClick}
                sx={{ 
                  mx: 1,
                  fontWeight: selectedCategory === null ? 700 : 400,
                  borderBottom: selectedCategory === null ? '3px solid white' : 'none',
                  borderRadius: 0,
                  paddingBottom: '5px'
                }}
              >
                Ana Sayfa
              </Button>
              
              {visibleCategories.map((category: Category) => (
                <Button
                  key={category.id}
                  color="inherit"
                  onClick={() => handleCategoryClick(category)}
                  sx={{ 
                    mx: 1,
                    fontWeight: selectedCategory === category.id ? 700 : 400,
                    borderBottom: selectedCategory === category.id ? '3px solid white' : 'none',
                    borderRadius: 0,
                    paddingBottom: '5px'
                  }}
                >
                  {category.name}
                </Button>
              ))}
              
              {dropdownCategories.length > 0 && (
                <Box>
                  <Button
                    color="inherit"
                    onClick={handleMenuOpen}
                    sx={{ mx: 1 }}
                  >
                    Diğer Kategoriler
                  </Button>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    keepMounted
                  >
                    {dropdownCategories.map((category: Category) => (
                      <MenuItem 
                        key={category.id} 
                        onClick={() => handleCategoryClick(category)}
                        selected={selectedCategory === category.id}
                      >
                        {category.name}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              )}
            </Box>
          )}

          {/* Mobile menu button */}
          {isMobile && (
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={toggleMobileMenu}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Drawer
            anchor="left"
            open={mobileMenuOpen}
            onClose={toggleMobileMenu}
          >
            <Box
              sx={{ width: 250 }}
              role="presentation"
            >
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                <Avatar 
                  src="/Akademikadro_Logo.jpeg"
                  alt="News Logo"
                  sx={{ 
                    width: 40, 
                    height: 40, 
                    marginRight: 1,
                    bgcolor: theme.palette.primary.main
                  }}
                >
                  N
                </Avatar>
                <Typography variant="h6" noWrap sx={{ lineHeight: 1, alignSelf: 'center' }}>
                Akademi Kadro
                </Typography>
              </Box>
              <Divider />
              <List>
                <ListItem onClick={handleHomeClick} sx={{ cursor: 'pointer' }}>
                  <ListItemText primary="Ana Sayfa" />
                </ListItem>
                {categories.map((category: Category) => (
                  <ListItem 
                    key={category.id} 
                    onClick={() => handleCategoryClick(category)}
                    sx={{ 
                      cursor: 'pointer',
                      bgcolor: selectedCategory === category.id ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
                      '&:hover': {
                        bgcolor: selectedCategory === category.id ? 'rgba(25, 118, 210, 0.15)' : 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    <ListItemText 
                      primary={category.name} 
                      primaryTypographyProps={{
                        fontWeight: selectedCategory === category.id ? 500 : 400
                      }}
                    />
                  </ListItem>
                ))}
              </List>
              <Divider />
            </Box>
          </Drawer>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar; 